import { PaymentMethodType } from "@/types/enums/payment-method-type"

const MEANINGLESS_LABELS = new Set(["none", "unknown", "0"])

function titleCaseWords(raw: string): string {
  return raw
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
}

/** Whether a raw API payment method/type value carries no customer-facing meaning. */
export function isMeaninglessPaymentMethod(raw: unknown): boolean {
  if (raw == null || raw === "") return true
  if (typeof raw === "number") return raw === PaymentMethodType.None || Number.isNaN(raw)
  const s = String(raw).trim()
  return !s || MEANINGLESS_LABELS.has(s.toLowerCase())
}

/** Maps API payment method/type values (enum, string, or display text) to a label. */
export function formatPaymentMethodLabel(raw: unknown): string | null {
  if (isMeaninglessPaymentMethod(raw)) return null

  if (typeof raw === "number") {
    const name = PaymentMethodType[raw]
    if (typeof name === "string" && name !== "None") return titleCaseWords(name)
    return null
  }

  const s = String(raw).trim()
  const enumKey = (s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()) as keyof typeof PaymentMethodType
  if (enumKey in PaymentMethodType) {
    const val = PaymentMethodType[enumKey]
    if (typeof val === "number" && val !== PaymentMethodType.None) return titleCaseWords(enumKey)
    return null
  }

  return titleCaseWords(s)
}

/** First meaningful label from ordered API candidates (e.g. payment_type, then primary_payment_method). */
export function resolvePaymentMethodDisplayLabel(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    const label = formatPaymentMethodLabel(candidate)
    if (label) return label
  }
  return null
}
